<template>
<div>
  <div class="col-shrink sidebar__header q-pb-xs">
    <div class="row q-mx-md q-mini-drawer-hide">
      <q-btn-dropdown flat dense class="col-5"
                      @hide="updateFilters">
        <template v-slot:label>
          <span class="text-weight-medium q-mr-sm ellipsis">Fases</span>
          <count-chip v-if="phaseFilter.length" :count="phaseFilter.length" />
        </template>
        <q-list style="min-width: 100px">
          <q-item v-for="phase in phases"
                  clickable
                  :key="phase.id">
            <q-checkbox v-model="phaseFilter"
                        :val="phase.id"
                        :label="phase.title"
                        @update:model-value="filtersUpdated = true" />
          </q-item>
        </q-list>
      </q-btn-dropdown>
      <q-btn-dropdown flat dense
                      class="col-6"
                      @hide="updateFilters">
        <template v-slot:label>
          <span class="text-weight-medium q-mr-sm ellipsis">Categorie&euml;n</span>
          <count-chip v-if="categoryFilter.length" :count="categoryFilter.length" />
        </template>
        <q-list style="min-width: 100px">
          <q-item v-for="category in categories"
                  clickable
                  :key="category.id">
            <q-checkbox v-model="categoryFilter"
                        :val="category.id"
                        :label="category.title"
                        @update:model-value="filtersUpdated = true" />
          </q-item>
        </q-list>
        <FlatButton :disable="allCategoriesSelected" @click="selectAllCategories">Selecteer alle</FlatButton>
        <FlatButton :disable="allCategoriesDeselected" @click="deselectAllCategories">Deselecteer alle</FlatButton>
      </q-btn-dropdown>
      <div class="col-1">
        <icon-button
            class="menu-button"
            :class="{'menu-button--alerted': hasHiddenFiltersActive}"
            icon="more_vert"
            text-color="neutral-90"
            size="sm"
            :aria-label="`Toon menu${ hasHiddenFiltersActive ? ' (verborgen filters)': ''}`"
            round>
          <actionable-menu @hide="updateFilters"
                           :autoClose="false"
                           separator>
            <q-item>
              <q-btn-dropdown flat dense
                              class="full-width">
                <template v-slot:label>
                  <span class="text-weight-medium q-mr-sm ">Type</span>
                  <count-chip v-if="typeFilter.length" :count="typeFilter.length" />
                </template>
                <q-list style="min-width: 100px">
                  <q-item v-for="type in ruleTypes"
                          clickable
                          :key="type.value">
                    <q-checkbox v-model="typeFilter"
                                :val="type.value"
                                :label="type.label"
                                @update:model-value="filtersUpdated = true" />
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-item>
            <q-item v-if="themes.length">
              <q-btn-dropdown flat dense
                              class="full-width">
                <template v-slot:label>
                  <span class="text-weight-medium q-mr-sm ">Thema's</span>
                  <count-chip v-if="themeFilter.length" :count="themeFilter.length" />
                </template>
                <q-list style="min-width: 100px">
                  <q-item v-for="theme in themes"
                          clickable
                          :key="theme.id">
                    <q-checkbox v-model="themeFilter"
                                :val="theme.id"
                                :label="theme.title"
                                @update:model-value="filtersUpdated = true" />
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-item>
            <q-item v-if="subcategories.length">
              <q-btn-dropdown flat dense
                              class="full-width">
                <template v-slot:label>
                  <span class="text-weight-medium q-mr-sm ">Subcategorie&euml;n</span>
                  <count-chip v-if="subcategoryFilter.length" :count="subcategoryFilter.length" />
                </template>
                <q-list style="min-width: 100px">
                  <q-item v-for="subcategory in subcategories"
                          clickable
                          :key="subcategory.id">
                    <q-checkbox v-model="subcategoryFilter"
                                :val="subcategory.id"
                                :label="subcategory.title"
                                @update:model-value="filtersUpdated = true" />
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-item>
            <q-item>
              <q-toggle
                  v-model="search_concept"
                  label="Richtlijnen in concept"
                  @update:model-value="filtersUpdated = true"
              />
            </q-item>
            <q-item>
              <q-toggle
                  v-model="search_archive"
                  label="Richtlijnen in het archief"
                  @update:model-value="filtersUpdated = true"
              />
            </q-item>
            <q-item v-if="activeFilters">
              <flat-button @click="resetFilter">
                Wis filter
              </flat-button>
            </q-item>
          </actionable-menu>
        </icon-button>
      </div>
    </div>
  </div>

  <div class="col q-mini-drawer-hide">
    <q-list separator class="tree tree--rule tree--filterable tree--full-height">
      <template v-for="rule in rules.data" :key="rule.id">
        <item-link :active="rule.id == current"
                   :href="$route('rule.show', {richtlijn: rule.id, page: rules.current_page })"
                   linkClasses="link link--primary q-pr-md">
          <q-item-section>
            <q-item-label lines="2">{{ rule.current_version.summary }}</q-item-label>
          </q-item-section>
          <q-item-section side top>
            <q-chip class="count-chip">{{ rule.code }}</q-chip>
          </q-item-section>
        </item-link>
      </template>
      <q-item v-if="rules.last_page > 1" class="flex flex-center">
        <pagination-links :items="rules" />
      </q-item>
    </q-list>
  </div>
</div>
</template>

<script>
import { ruleType } from '~HIOR/constants'
import IconButton from '../button/IconButton'
import FlatButton from '../button/FlatButton.vue'

export default {
  components: { FlatButton, IconButton },
  props: {
    rules: {
      type: Object,
      default: () => ({}),
    },
    phases: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    subcategories: {
      type: Array,
      required: true,
    },
    themes: {
      type: Array,
      required: true,
    },
    filters: {
      type: Object,
    },
  },
  data () {
    return {
      phaseFilter: this.filters?.phase_ids || [],
      categoryFilter: this.filters?.category_ids || [],
      typeFilter: this.filters?.types || [],
      themeFilter: this.filters?.theme_ids || [],
      subcategoryFilter: this.filters?.subcategory_ids || [],
      search: this.filters?.search || '',
      search_concept: !!this.filters?.search_concept,
      search_archive: !!this.filters?.search_archive,
      filtersUpdated: false,
      ruleTypes: [
        {
          value: ruleType.TYPE_NORMAL,
          label: 'Standaard',
        },
        {
          value: ruleType.TYPE_FLEXIBLE,
          label: 'Flexibel',
        },
        {
          value: ruleType.TYPE_CUSTOM,
          label: 'Project-specifiek',
        },
      ],
    }
  },
  computed: {
    current () {
      const id = this.$route().params.rule_id || this.$route().params.richtlijn
      if (!id) return null
      return parseInt(id)
    },
    hasHiddenFiltersActive () {
      return this.typeFilter.length ||
          this.themeFilter.length ||
          this.subcategoryFilter.length ||
          this.search_concept ||
          this.search_archive
    },
    activeFilters () {
      return this.phaseFilter.length ||
          this.categoryFilter.length ||
          this.themeFilter.length ||
          this.subcategoryFilter.length ||
          this.typeFilter.length ||
          this.search ||
          this.search_concept ||
          this.search_archive
    },
    allCategoriesSelected () {
      return this.categories.length === this.categoryFilter.length
    },
    allCategoriesDeselected () {
      return this.categoryFilter.length === 0
    },
  },
  methods: {
    async updateFilters () {
      if (!this.filtersUpdated) return
      try {
        await axios.post(this.$route('filter.set', { key: 'filter-rule' }), {
          phase_ids: this.phaseFilter,
          category_ids: this.categoryFilter,
          subcategory_ids: this.subcategoryFilter,
          theme_ids: this.themeFilter,
          types: this.typeFilter,
          search: this.search,
          search_concept: this.search_concept,
          search_archive: this.search_archive,
        })
        location.reload()
      } catch (e) {
        // ...
      }
    },
    selectAllCategories () {
      this.filtersUpdated = true
      this.categoryFilter = this.categories.map(({ id }) => id)
    },
    deselectAllCategories () {
      this.filtersUpdated = true
      this.categoryFilter = []
    },
    async resetFilter () {
      try {
        await axios.post(this.$route('filter.clear', { key: 'filter-rule' }))
        location.reload()
      } catch (e) {
        // ...
      }
    },
    doSearch (term) {
      if (term === undefined || this.search === term) {
        return
      }
      this.filtersUpdated = true
      this.search = term
      this.updateFilters()
    },
  },
}
</script>
